/**
 * @file Disco-daily utilities.
 * @copyright Copyright (c) 2020-2026 Woolly Bear Software LLC,
 *            all rights reserved.
 */
import { Cache, PREFER_SPOTIFY_APP_CACHE } from "./cache";
import { AVAILABLE_YEARS, IS_PUBLIC, RUN_MODE } from "./constants";

export interface SelectOption {
  value: string | number;
  label?: string;
  options?: SelectOption[];
}

/**
 * Sleep for a bit.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Generate a client-side unique id (for mixes/models). Prefers
 * crypto.randomUUID() where available, falling back to a simple RFC4122-ish
 * generator - crypto.randomUUID isn't implemented in every environment this
 * code runs in (e.g. Jest's jsdom), and this is only ever used as a local
 * identifier, not a security-sensitive value.
 */
export function generateId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Convert milliseconds to hh:mm:ss
 */
export function msToTime(
  duration: number,
  spellit = false,
  showSeconds = true,
): string {
  let seconds = Math.floor((duration / 1000) % 60);
  let minutes = Math.floor((duration / (1000 * 60)) % 60);
  let hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

  if (!spellit) {
    hours = Number(hours < 10 ? `0${hours}` : hours);
    minutes = Number(minutes < 10 ? `0${minutes}` : minutes);
    seconds = Number(seconds < 10 ? `0${seconds}` : seconds);
  }

  if (spellit) {
    const dateParts: string[] = [];

    if (hours > 0) {
      dateParts.push(`${hours} hr`);
    }
    if (minutes > 0) {
      dateParts.push(`${minutes} min`);
    }
    if (showSeconds && seconds > 0) {
      dateParts.push(`${seconds} sec`);
    }
    return dateParts.join(" ");
  }
  return `${hours}:${minutes}:${seconds}`;
}

/**
 * Get a date string in mm-dd-yy format.
 */
export function getShortDateStr(): string {
  const d = new Date();
  const year = d.getFullYear().toString().slice(-2);
  let month: string | number = d.getMonth() + 1;
  let day: string | number = d.getDate();

  if (String(month).length === 1) {
    month = `0${month}`;
  }

  if (String(day).length === 1) {
    day = `0${day}`;
  }

  return `${month}-${day}-${year}`;
}

export const roundTo = (n: number, to: number): number => n - (n % to);

/**
 * Get route URI with runmode prefix.
 */
export function getRouteURI(path: string): string {
  if (RUN_MODE === "production" && !IS_PUBLIC) {
    return `/prod/api${path}`;
  }
  if (RUN_MODE === "development" && !IS_PUBLIC) {
    return `/dev/api${path}`;
  }
  return `/api${path}`;
}

/**
 * Get router base path.
 */
export function getRouterBase(): string {
  if (RUN_MODE === "production" && !IS_PUBLIC) {
    return "/prod/";
  }
  if (RUN_MODE === "development" && !IS_PUBLIC) {
    return "/dev/";
  }
  return "/";
}

/**
 * Flatten a tree of option values.
 */
export function flattenOptions(
  opts: SelectOption[],
  includeCategory = false,
): Array<string | number | (string | number)[]> {
  return opts.flatMap((option) => {
    if (!option.options) {
      return option.value;
    }
    const subOptions = option.options.map((subOption) => subOption.value);
    if (includeCategory) {
      return [option.value, ...subOptions];
    }
    return subOptions;
  });
}

/**
 * Capitalize words in a string.
 */
export function capitalizeWords(str: string): string {
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Scroll to top of page.
 */
export function scrollTop(): void {
  window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
}

/**
 * Get all years in a decade that are available.
 */
export function getDecadeYears(decade: number): number[] {
  const decadeYears = Array.from({ length: 10 }, (_, i) => decade + i);
  return decadeYears.filter((year) => AVAILABLE_YEARS.includes(year));
}

/**
 * Resolve which Spotify link to use for a click - the native app (via its
 * spotify: URI scheme) if the user has opted into that (Account settings)
 * and one is available, otherwise the normal web player URL. Desktop-app
 * links have no OS-level "app not installed" fallback (unlike mobile's
 * automatic universal-link handoff), which is why this is opt-in rather
 * than automatic.
 * @param webUrl The open.spotify.com fallback URL.
 * @param spotifyUri The spotify:... URI, or null if one can't be built
 *     (e.g. a profile with no id).
 * @return The href to actually use.
 */
export function getSpotifyLinkHref(
  webUrl: string,
  spotifyUri: string | null,
): string {
  return resolveSpotifyLink(webUrl, spotifyUri).href;
}

export interface SpotifyLinkResolution {
  href: string;
  // Whether href is the spotify: app URI (vs. the open.spotify.com web
  // URL) - callers use this to decide target/rel: a new tab for the web
  // URL (real content loads there), but same-tab navigation for the app
  // URI, since target="_blank" for a custom URI scheme just leaves a
  // permanently blank tab behind once the OS hands off to the app.
  isAppLink: boolean;
}

/**
 * Same resolution as getSpotifyLinkHref, but also reports whether the app
 * URI was chosen, for callers that need to adjust target/rel accordingly.
 * @param webUrl The open.spotify.com fallback URL.
 * @param spotifyUri The spotify:... URI, or null if one can't be built.
 * @return The resolved href and whether it's the app link.
 */
export function resolveSpotifyLink(
  webUrl: string,
  spotifyUri: string | null,
): SpotifyLinkResolution {
  if (spotifyUri && Cache.read<boolean>(PREFER_SPOTIFY_APP_CACHE)) {
    return { href: spotifyUri, isAppLink: true };
  }
  return { href: webUrl, isAppLink: false };
}

/**
 * Point a Spotify track URL/URI at a specific playlist context, so opening
 * it highlights/scrolls to the track within that playlist instead of just
 * showing the track's own page. The app URI scheme and the web player use
 * different shapes for this (confirmed by hand - the web player's variant
 * doesn't work as a "context" param on the track URL itself, unlike the
 * app): the app URI appends "?context=spotify:playlist:{playlistId}" to
 * the track URI, while the web URL has to be rebuilt as the *playlist's*
 * URL with a "?highlight=spotify:track:{trackId}" param instead. No-op if
 * playlistId is null.
 * @param url The track's web URL or spotify: URI.
 * @param trackId The track's plain Spotify id (needed for the web
 *     player's highlight param, since that variant isn't just an append).
 * @param playlistId The playlist to link the track's context to.
 * @return The resolved url, or url unchanged if playlistId is null.
 */
export function withPlaylistContext(
  url: string,
  trackId: string,
  playlistId: string | null,
): string {
  if (!playlistId) {
    return url;
  }
  if (url.startsWith("spotify:")) {
    return `${url}?context=spotify:playlist:${playlistId}`;
  }
  return `https://open.spotify.com/playlist/${playlistId}?highlight=spotify:track:${trackId}`;
}

/**
 * Soft heuristic for "is this most likely a desktop browser" - used only
 * to decide whether to offer the one-time "Open Spotify Links in App?"
 * dialog (spotify-link-prompt.tsx). Mobile already gets the native app
 * automatically via OS universal links, so there's nothing useful to
 * offer there. A wrong guess here just means someone isn't offered the
 * dialog (recoverable via the Account settings toggle), not a functional
 * break, so a plain userAgent check is good enough - no need for
 * anything more precise.
 * @return Whether this looks like a desktop browser.
 */
export function isLikelyDesktop(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !/Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
  );
}
